import { useI18n } from 'vue-i18n';
import { toRaw } from 'vue';
import { dateUtils } from './date-utils';
import { AppCoreEnums } from 'src/core/shared/enums';
import { IFilterItem } from '../../stores/core/models';
import useDateUtils from '../composable/use-date-utils';
import { IDateRange, IDynamicFilterItem, IDynamicFilterValueOptions, ISelectItem } from 'src/stores/core/models';

const dynamicFiltersUtils = {
  getChipFilterOperator: (filterItem: IDynamicFilterItem) => {
    const dateRange = filterItem.value as IDateRange;
    switch (filterItem.type) {
      case AppCoreEnums.FilterItemType.Select:
      case AppCoreEnums.FilterItemType.Date:
      case AppCoreEnums.FilterItemType.DateWithTime: {
        if (dateRange.rangeOption === AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom) {
          if (dateRange.rangeOption) {
            if (dateRange.from && !dateRange.to) return ' >= ';
            if (!dateRange.from && dateRange.to) return ' <= ';

            const { t } = useI18n();
            return t('In').toString();
          }
        } else {
          return ' == ';
        }
        return;
      }
      case AppCoreEnums.FilterItemType.MultiSelect:
      case AppCoreEnums.FilterItemType.Text:
        return ' == ';

      default:
        return ' - ';
    }
  },

  getChipFilterItemValue: (filterItem: IDynamicFilterItem, filterValueOptions: ISelectItem[] | undefined) => {
    const dateRange = filterItem.value as IDateRange;
    const { t } = useI18n();
    switch (filterItem.type) {
      case AppCoreEnums.FilterItemType.Select: {
        const relatedOption = filterValueOptions?.find((x) => x.value === filterItem.key);

        return t(relatedOption?.label || filterItem.valueResourceId?.toString() || '');
      }

      case AppCoreEnums.FilterItemType.MultiSelect: {
        const filterValueItems = filterItem.value as string[];
        if (filterValueOptions?.length === filterValueItems.length) return t('core.all');
        if (filterValueItems.length > 1) {
          let selectedTransl = t('core.selected').toString();
          selectedTransl = selectedTransl.charAt(0).toLowerCase() + selectedTransl.slice(1);
          return `${filterValueItems.length} ${selectedTransl}`;
        }

        const relatedOption = filterValueOptions?.find((x) => x.value === filterItem.key);

        return t(relatedOption?.label || filterItem.valueResourceId?.toString() || '');
      }

      case AppCoreEnums.FilterItemType.Date: {
        if (dateRange.rangeOption === AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom) {
          const from = dateUtils.formatDateToCulture(dateRange.from as string);
          const to = dateUtils.formatDateToCulture(dateRange.to as string);
          if (dateRange.from && !dateRange.to) return from;
          if (!dateRange.from && dateRange.to) return to;
          return from + ' - ' + to;
        }

        return t(dateRange.rangeOptionLabelResourceId).toString();
      }

      case AppCoreEnums.FilterItemType.DateWithTime: {
        if (dateRange.rangeOption === AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom) {
          const { formatDateTime } = useDateUtils();
          const from = formatDateTime(dateRange.from as string);
          const to = formatDateTime(dateRange.to as string);
          if (dateRange.from && !dateRange.to) return from;
          if (!dateRange.from && dateRange.to) return to;
          return from + ' - ' + to;
        }

        return t(dateRange.rangeOptionLabelResourceId).toString();
      }

      case AppCoreEnums.FilterItemType.Text: {
        if ((filterItem.value as string)?.length > 20) {
          return (filterItem.value as string).substring(0, 20) + '...';
        } else {
          return filterItem.value;
        }
      }
    }
  },

  getChipFilterTitle: (filterItem: IDynamicFilterItem, filterValueOptions: ISelectItem[] | undefined) => {
    const { t } = useI18n();
    return `${t(filterItem.keyResourceId)} ${dynamicFiltersUtils.getChipFilterOperator(
      filterItem
    )} ${dynamicFiltersUtils.getChipFilterItemValue(filterItem, filterValueOptions)}`;
  },

  // Dynamic Filter Item Date

  getResourceIdBasedOnRangeOption: (rangeSelectedOption: AppCoreEnums.DynamicFilterDateRangeOptionsEnum) => {
    switch (rangeSelectedOption) {
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Today:
        return 'Today';
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Yesterday:
        return 'Yesterday';
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last2Days:
        return 'Last2Days';
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last7Days:
        return 'Last7Days';
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last15Days:
        return 'Last15Days';
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom:
        return 'Custom';
      default:
        return '';
    }
  },

  getDynamicFilterValueResourceId: (
    selectedFilterValueOptions: IDynamicFilterValueOptions | undefined,
    selectedFilterValue: string | string[] | IDateRange | undefined
  ) => {
    switch (selectedFilterValueOptions?.type) {
      case AppCoreEnums.FilterItemType.Select:
        return selectedFilterValueOptions?.options?.find((x) => x.value === selectedFilterValue)?.label || '';
      case AppCoreEnums.FilterItemType.MultiSelect:
        return selectedFilterValueOptions?.options?.find((x) => x.value === (selectedFilterValue as string[])[0])?.label || '';
      default:
        return selectedFilterValue;
    }
  },

  prepareDynamicFilterItem: (dynamicFilter: IDynamicFilterValueOptions, value: string[]) =>
    <IDynamicFilterItem>{
      key: dynamicFilter.key,
      value: value,
      keyResourceId: dynamicFilter.label,
      valueResourceId: dynamicFiltersUtils.getDynamicFilterValueResourceId(dynamicFilter, value),
      type: dynamicFilter.type,
      isStickyInternal: dynamicFilter.isSticky || false,
    },

  setDatesBasedOnRangeOption: (
    rangeOption: AppCoreEnums.DynamicFilterDateRangeOptionsEnum,
    customDateRange: IDateRange | null | undefined = undefined,
    includeTime: Boolean = true
  ) => {
    const now = new Date();
    const range = <IDateRange>{};

    switch (rangeOption) {
      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Today:
        now.setHours(0, 0, 0, 0);
        if (includeTime) {
          range.from = now.toISOString();
          now.setHours(23, 59, 59, 999);
          range.to = now.toISOString();
        } else {
          range.from = dateUtils.convertDateToIsoStringWithoutOffset(now);
          range.to = range.from;
        }
        break;

      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Yesterday:
        now.setDate(now.getDate() - 1);
        if (includeTime) {
          now.setHours(0, 0, 0, 0);
          range.from = now.toISOString();
          now.setHours(23, 59, 59, 999);
          range.to = now.toISOString();
        } else {
          range.from = dateUtils.convertDateToIsoStringWithoutOffset(now);
          range.to = range.from;
        }
        break;

      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last2Days:
        now.setDate(now.getDate() - 2);

        if (includeTime) {
          range.from = now.toISOString();
          range.to = new Date().toISOString();
        } else {
          range.from = dateUtils.convertDateToIsoStringWithoutOffset(now);
          range.to = dateUtils.convertDateToIsoStringWithoutOffset(new Date());
        }
        break;

      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last7Days:
        now.setDate(now.getDate() - 7);

        if (includeTime) {
          range.from = now.toISOString();
          range.to = new Date().toISOString();
        } else {
          range.from = dateUtils.convertDateToIsoStringWithoutOffset(now);
          range.to = dateUtils.convertDateToIsoStringWithoutOffset(new Date());
        }
        break;

      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last15Days:
        now.setDate(now.getDate() - 15);

        if (includeTime) {
          range.from = now.toISOString();
          range.to = new Date().toISOString();
        } else {
          range.from = dateUtils.convertDateToIsoStringWithoutOffset(now);
          range.to = dateUtils.convertDateToIsoStringWithoutOffset(new Date());
        }
        break;

      case AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom:
        range.from = customDateRange?.from as string;
        range.to = customDateRange?.to as string;
        break;
    }

    return range;
  },

  removeNullProperties: (obj: any) => {
    return (
      Object.entries(obj)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, v]) => v != null)
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
    );
  },

  convertObjectToFilterItems: (filters: any, filterTypes: any) => {
    filters = { ...dynamicFiltersUtils.removeNullProperties(toRaw(filters)) };

    return Object.keys(filters).map((x) => {
      return {
        fieldName: x,
        values: Array.isArray(filters[x]) ? filters[x] : [filters[x]],
        filterType: filterTypes[x],
      } as IFilterItem;
    });
  },

  convertDynamicFiltersToFilterItems: (dynamicFilters: IDynamicFilterItem[]) => {
    const dynamicFiltersFlatten = {} as any;
    const dynamicFiltersFlattenTypes = {} as any;

    let range: IDateRange;
    let rangeFromTo: IDateRange;
    dynamicFilters?.forEach((x) => {
      switch (x.type) {
        case AppCoreEnums.FilterItemType.DateWithTime: {
          range = { ...(x.value as IDateRange) };
          rangeFromTo = dynamicFiltersUtils.setDatesBasedOnRangeOption(range.rangeOption, range, true);
          range.from = rangeFromTo.from;
          range.to = rangeFromTo.to;
          dynamicFiltersFlatten[x.key] = [range.from, range.to];
          break;
        }

        case AppCoreEnums.FilterItemType.Date: {
          range = { ...(x.value as IDateRange) };
          rangeFromTo = dynamicFiltersUtils.setDatesBasedOnRangeOption(range.rangeOption, range, false);
          range.from = rangeFromTo.from;
          range.to = rangeFromTo.to;
          dynamicFiltersFlatten[x.key] = [range.from, range.to];
          break;
        }

        default:
          dynamicFiltersFlatten[x.key] = x.value;
          break;
      }
      dynamicFiltersFlattenTypes[x.key] = x.type;
    });

    return dynamicFiltersUtils.convertObjectToFilterItems(dynamicFiltersFlatten, dynamicFiltersFlattenTypes);
  },
};

export { dynamicFiltersUtils };
